import React from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme, SimpleButton } from '../../../../ui';
import { shadeColor, toRgba } from '../../../../ui/theme/colors';
import Icon from '../../../../ui/Icon';

const WidgetContainer = styled(Flex)`
  background: ${theme.colors.NEU_0};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px ${toRgba(theme.colors.NEU_999, 0.04)};
  width: 100%;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const DeadlinesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 16px;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const DeadlineItem = styled(Flex)`
  background: ${theme.colors.NEU_0};
  border: 1px solid ${theme.colors.NEU_200};
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px ${toRgba(theme.colors.NEU_999, 0.08)};
    border-color: ${(props) => {
      if (props.$priority === 'high') return theme.colors.ACTION;
      if (props.$priority === 'medium') return theme.colors.SECONDARY;
      return theme.colors.NEU_300;
    }};
  }
`;

const IconCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props) => {
    if (props.$priority === 'high') return shadeColor(theme.colors.ACTION, 0.92);
    if (props.$priority === 'medium') return shadeColor(theme.colors.SECONDARY, 0.92);
    return theme.colors.NEU_100;
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const DaysLeft = styled.div`
  padding: 4px 12px;
  border-radius: 12px;
  background: ${(props) => {
    if (props.$daysLeft <= 2) return shadeColor(theme.colors.ACTION, 0.92);
    if (props.$daysLeft <= 5) return shadeColor(theme.colors.SECONDARY, 0.92);
    return theme.colors.NEU_100;
  }};
  color: ${(props) => {
    if (props.$daysLeft <= 2) return theme.colors.ACTION;
    if (props.$daysLeft <= 5) return theme.colors.SECONDARY;
    return theme.colors.NEU_600;
  }};
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;

/**
 * UpcomingDeadlinesWidget Component
 *
 * Displays upcoming deadlines for assignments, quizzes, and course modules.
 *
 * Features:
 * - Priority-based color coding
 * - Days remaining calculation
 * - Task type icons
 * - Quick action buttons
 */
const UpcomingDeadlinesWidget = ({ deadlines = [] }) => {
  if (!deadlines || deadlines.length === 0) {
    return null;
  }

  const calculateDaysLeft = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getTaskIcon = (taskType) => {
    switch (taskType) {
      case 'assignment':
        return 'File';
      case 'quiz':
        return 'Quiz';
      case 'module':
        return 'Course';
      default:
        return 'Calendar';
    }
  };

  // Sort by due date (soonest first)
  const sortedDeadlines = [...deadlines]
    .filter((d) => !d.completed)
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
    .slice(0, 5); // Show max 5

  return (
    <WidgetContainer direction='column' align='flex-start'>
      <Flex align='center' justify='space-between' width='100%' style={{ marginBottom: '20px' }}>
        <Flex direction='column' align='flex-start'>
          <Typo.ContentSmallHeader $textAlign='left' color={theme.colors.NEU_PRIMARY}>
            Upcoming Deadlines
          </Typo.ContentSmallHeader>
          <Typo.Info color={theme.colors.NEU_600}>
            {sortedDeadlines.length} task{sortedDeadlines.length !== 1 ? 's' : ''} pending
          </Typo.Info>
        </Flex>
        <Icon.Time size={'medium'} color={theme.colors.PRIMARY} />
      </Flex>

      <DeadlinesList>
        {sortedDeadlines.map((deadline) => {
          const daysLeft = calculateDaysLeft(deadline.dueDate);
          const TaskIcon = Icon[getTaskIcon(deadline.taskType)];

          return (
            <DeadlineItem key={deadline.id} align='flex-start' gap='12' $priority={deadline.priority}>
              <IconCircle $priority={deadline.priority}>
                {TaskIcon && (
                  <TaskIcon
                    size={'small'}
                    color={deadline.priority === 'high' ? theme.colors.ACTION : deadline.priority === 'medium' ? theme.colors.SECONDARY : theme.colors.NEU_600}
                  />
                )}
              </IconCircle>

              <Flex direction='column' align='flex-start' style={{ flex: 1 }}>
                <Typo.MediumText fontWeight='600' color={theme.colors.NEU_PRIMARY} $textAlign='left'>
                  {deadline.taskTitle}
                </Typo.MediumText>
                <Typo.Info color={theme.colors.NEU_600} $textAlign='left'>
                  {deadline.courseTitle}
                </Typo.Info>
                <Flex align='center' gap='8' style={{ marginTop: '8px' }}>
                  <Icon.Calendar size={'x-small'} color={theme.colors.NEU_600} />
                  <Typo.Info color={theme.colors.NEU_600} style={{ fontSize: '11px' }}>
                    Due {new Date(deadline.dueDate).toLocaleDateString()}
                  </Typo.Info>
                </Flex>
              </Flex>

              <Flex direction='column' align='flex-end' gap='8'>
                <DaysLeft $daysLeft={daysLeft}>
                  {daysLeft === 0 ? 'Today' : daysLeft === 1 ? 'Tomorrow' : daysLeft < 0 ? 'Overdue' : `${daysLeft} days`}
                </DaysLeft>
                <SimpleButton size='small' onClick={() => console.log('Start task:', deadline.id)} style={{ fontSize: '11px', padding: '4px 12px' }}>
                  Start
                </SimpleButton>
              </Flex>
            </DeadlineItem>
          );
        })}
      </DeadlinesList>

      {deadlines.filter((d) => !d.completed).length > 5 && (
        <Flex justify='center' width='100%' style={{ marginTop: '16px' }}>
          <SimpleButton variant='outlined' size='small'>
            View All Deadlines
          </SimpleButton>
        </Flex>
      )}
    </WidgetContainer>
  );
};

export default UpcomingDeadlinesWidget;
